import { Dispatch, SetStateAction, useState } from 'react';

interface UseSearchDraftReturn {
  /** What the search box should be showing. */
  draft: string;
  setDraft: Dispatch<SetStateAction<string>>;
  /** `key` for the search box — changes only when the box has to be remounted. */
  inputKey: number;
}

/**
 * Draft state for a search box whose committed value lives outside the component — in the URL, in
 * practice.
 *
 * ```tsx
 * const { draft, setDraft, inputKey } = useSearchDraft(urlState.searchValue ?? '');
 *
 * <SearchInput key={inputKey} value={draft} onChange={(e) => setDraft(e.target.value)} />
 * ```
 *
 * The rules:
 *
 * - While the user types, the draft is theirs.
 * - When the committed value changes to something the box is not showing, the draft snaps to it, so
 *   the box can never display a term that is not applied to the results below it.
 * - `inputKey` changes on that snap and nowhere else, so typing and submitting keep focus.
 *
 * Two things make this necessary. The committed value can change without this component unmounting
 * (back navigation, re-entering the tab, re-tapping the active tab), which is what strands an
 * uncommitted term on screen. And Liquidity's `SearchInput` seeds its internal value from the
 * `value` prop on mount and never syncs to it again, so remounting is the only way to push a value
 * into it.
 */
export function useSearchDraft(committedValue: string): UseSearchDraftReturn {
  const [draft, setDraft] = useState<string>(committedValue);
  const [trackedCommittedValue, setTrackedCommittedValue] = useState<string>(committedValue);
  const [inputKey, setInputKey] = useState<number>(0);

  if (trackedCommittedValue !== committedValue) {
    setTrackedCommittedValue(committedValue);

    // Reason: on the submit path the draft already matches, and remounting there would only cost
    // the user their cursor. Only remount when the box is showing something else.
    if (draft !== committedValue) {
      setDraft(committedValue);
      setInputKey((key) => key + 1);
    }
  }

  return { draft, setDraft, inputKey };
}
